import type { Evidence, FindingDetail, FindingStatus, MeterRead } from "@riea-water/contracts";
import { useRef, useState, type FormEvent } from "react";
import { Link, useOutletContext, useParams } from "react-router";
import {
  useFieldJob,
  useFinding,
  useLinkEvidence,
  useReads,
  useRecordExchange,
  useSupplyPoint,
  useTransitionFinding,
  useUploadEvidence,
} from "../api/queries.js";
import { needsExchangeRecorded } from "./field-helpers.js";
import { RaiseJobSheet, raisedMessage } from "./RaiseJobSheet.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";
import {
  EVIDENCE_KIND_LABEL,
  factRows,
  impactLine,
  missingKinds,
  SEVERITY_TONE,
  shortDate,
  slaText,
  STATUS_LABEL,
  statusTone,
  stepsDone,
} from "./findings-helpers.js";

/** A form field as text; a missing field is the empty string. */
function formText(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value : "";
}

/**
 * Data Assurance · Finding (FND-01 to FND-05, AC-2-07), as the prototype
 * draws it: the pills and reference, Why this was raised with the facts,
 * Owner and SLA with the impact formula, Route to fix with its done ticks,
 * Evidence to close with needed against attached, the Move to buttons from
 * the state machine, and Resolve disabled until the evidence is complete.
 */
export function Finding() {
  const { id } = useParams();
  const { client, me, getToken } = useOutletContext<ShellContext>();
  const finding = useFinding(client, id ?? null);
  const upload = useUploadEvidence(getToken);
  const transition = useTransitionFinding(client);
  const link = useLinkEvidence(client);
  const { toast, Toast } = useToast();
  const moveDialog = useRef<HTMLDialogElement>(null);
  const attachDialog = useRef<HTMLDialogElement>(null);
  const [pendingMove, setPendingMove] = useState<FindingStatus | null>(null);
  const [attachKind, setAttachKind] = useState<string | null>(null);
  const raiseDialog = useRef<HTMLDialogElement>(null);
  const recordExchange = useRecordExchange(client);
  // WM-R06's route goes back to the exchange job the registry never heard about: the job its
  // field record belongs to (D-61).
  const exchangeJobId =
    finding.data?.ruleId === "WM-R06"
      ? ([...finding.data.heldEvidence, ...finding.data.evidence]
          .filter((e) => e.kind === "field_record")
          .flatMap((e) => e.attachedTo)
          .find((t) => t.type === "field_job")?.id ?? null)
      : null;
  const exchangeJob = useFieldJob(client, exchangeJobId);
  const canRaise = ["tenant_admin", "data_lead", "analyst", "field_coordinator"].includes(me.role);
  const canComplete = ["tenant_admin", "data_lead", "field_coordinator"].includes(me.role);

  if (finding.isPending) {
    return (
      <>
        <div className="crumb">
          <Link to="/findings">Findings</Link>
          <span className="faint">/</span>
          <span>…</span>
        </div>
        <div className="skeleton" />
      </>
    );
  }
  if (finding.isError || !finding.data) {
    return (
      <>
        <div className="crumb">
          <Link to="/findings">Findings</Link>
          <span className="faint">/</span>
          <span>Not found</span>
        </div>
        <div className="empty">
          {finding.error?.message.includes("404") || finding.error?.message.includes("no finding")
            ? "Finding not found."
            : `The finding could not be loaded: ${finding.error?.message ?? "unknown error"}.`}
        </div>
      </>
    );
  }
  const f = finding.data;
  const missing = missingKinds(f);
  const done = stepsDone(f.status, f.route.length);
  const sla = slaText(f);
  const nexts = f.allowedTransitions;

  const move = (to: FindingStatus, extra: Record<string, string | undefined> = {}) => {
    transition.mutate(
      { findingId: f.id, to, ...extra },
      {
        onSuccess: (moved) => {
          moveDialog.current?.close();
          setPendingMove(null);
          toast(
            `Moved from ${STATUS_LABEL[f.status]} to ${STATUS_LABEL[moved.status]} · FindingTransitioned recorded`,
          );
        },
        onError: (error) => toast(`Move refused · ${error.message}`),
      },
    );
  };

  const onMove = (to: FindingStatus) => {
    if (to === "dismissed" || to === "awaiting_party" || to === "reopened") {
      setPendingMove(to);
      moveDialog.current?.showModal();
      return;
    }
    if (to === "assigned") {
      move(to, { assigneeUserId: me.userId });
      return;
    }
    move(to);
  };

  const onMoveForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pendingMove) return;
    const form = new FormData(event.currentTarget);
    move(pendingMove, {
      reason: formText(form, "reason") || undefined,
      reasonCode: formText(form, "reasonCode") || undefined,
      awaitingRef: formText(form, "awaitingRef") || undefined,
    });
  };

  const attach = (input: { evidenceId?: string; readId?: string }) => {
    link.mutate(
      { findingId: f.id, ...input },
      {
        onSuccess: () => {
          attachDialog.current?.close();
          setAttachKind(null);
          toast(
            `${attachKind ? (EVIDENCE_KIND_LABEL[attachKind] ?? attachKind) : "Evidence"} attached · FindingEvidenceLinked recorded`,
          );
        },
        onError: (error) => toast(`Attach refused · ${error.message}`),
      },
    );
  };

  return (
    <>
      <div className="crumb">
        <Link to="/findings">Findings</Link>
        <span className="faint">/</span>
        <span>{f.ref}</span>
      </div>
      <div className="head">
        <div>
          <div className="split">
            <span className={`pill ${SEVERITY_TONE[f.severity]}`}>{f.severity}</span>
            <span className={`pill ${statusTone(f.status)}`}>{STATUS_LABEL[f.status]}</span>
            <span className="mono faint" style={{ fontSize: ".72rem" }}>
              {f.ref} · {f.ruleId}
            </span>
          </div>
          <h1 style={{ marginTop: 6 }}>{f.ruleName}</h1>
        </div>
        {f.spid ? (
          <Link className="btn sm" to={`/supply-points/${f.spid}`}>
            Open {f.spid}
          </Link>
        ) : null}
      </div>

      <div className="g2">
        <section className="card">
          <header>
            <h2>Why this was raised</h2>
            <span className="faint mono" style={{ fontSize: ".72rem" }}>
              first raised {shortDate(f.firstRaisedAt)}
            </span>
          </header>
          <div className="body">
            <p style={{ margin: "0 0 12px" }}>{f.explanation}</p>
            {factRows(f.facts).map((row) => (
              <div className="kv" key={row.label}>
                <span className="k">{row.label}</span>
                <span className="v">{row.value}</span>
                <span />
              </div>
            ))}
          </div>
        </section>
        <section className="card">
          <header>
            <h2>Owner and SLA</h2>
          </header>
          <div className="body">
            <div className="kv">
              <span className="k">Owner</span>
              <span className="v">
                {f.ownerTeamName ?? f.ownerPartyName}
                <small>
                  {f.ownerTeamName ? f.ownerPartyName : "Retailer"}
                  {f.assigneeName ? ` · assigned to ${f.assigneeName}` : ""}
                </small>
              </span>
              <span />
            </div>
            <div className="kv">
              <span className="k">SLA</span>
              <span
                className="v"
                style={
                  sla.tone === "late"
                    ? { color: "var(--crit)", fontWeight: 600 }
                    : sla.tone === "soon"
                      ? { color: "var(--warn)" }
                      : undefined
                }
              >
                {sla.label}
                <small>due {shortDate(f.slaDueAt)}</small>
              </span>
              <span />
            </div>
            <div className="kv">
              <span className="k">Impact</span>
              <span className="v">
                {f.impactScore.toFixed(1)}
                <small>
                  severity × exposure × volume × age ÷ effort · {impactLine(f)} · assumptions until
                  a pilot calibrates them
                </small>
              </span>
              <span />
            </div>
            <div className="kv">
              <span className="k">Market measure</span>
              <span className="v">
                {f.impactFactors.exposureFactor > 1
                  ? "Relevant to market performance"
                  : "Not linked"}
              </span>
              <span />
            </div>
          </div>
        </section>
      </div>

      <div className="g2">
        <section className="card">
          <header>
            <h2>Route to fix</h2>
          </header>
          <div className="body">
            <div className="steps">
              {f.route.map((step, i) => (
                <div className={`step${i < done ? " done" : ""}`} key={step}>
                  <b>{i < done ? "✓" : i + 1}</b>
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <div className="split" style={{ marginTop: 14 }}>
              {canRaise ? (
                <button
                  className="btn sm"
                  type="button"
                  onClick={() => raiseDialog.current?.showModal()}
                >
                  Raise field job
                </button>
              ) : null}
              {exchangeJob.data && needsExchangeRecorded(exchangeJob.data) && canComplete ? (
                <button
                  className="btn sm primary"
                  type="button"
                  disabled={recordExchange.isPending}
                  onClick={() =>
                    recordExchange.mutate(exchangeJob.data.id, {
                      onSuccess: (job) =>
                        toast(
                          `${job.ref} · MeterExchanged recorded with the job as evidence · linked findings re-checked`,
                        ),
                      onError: (e) => toast(`Not recorded · ${e.message}`),
                    })
                  }
                >
                  Record the exchange {exchangeJob.data.ref}
                </button>
              ) : null}
              <button
                className="btn sm"
                type="button"
                disabled
                title="Bilateral drafts arrive in Phase 5"
              >
                Draft bilateral
              </button>
            </div>
          </div>
        </section>
        <section className="card">
          <header>
            <h2>Evidence to close</h2>
            <span className="mono faint" style={{ fontSize: ".72rem" }}>
              {f.closeRequires.length - missing.length} of {f.closeRequires.length}
            </span>
          </header>
          <div className="body">
            {f.closeRequires.map((kind) => {
              const attached = f.evidence.find((e) => e.kind === kind);
              return (
                <div className="check" key={kind}>
                  <span>
                    {attached ? (
                      <span className="pill ok">attached</span>
                    ) : (
                      <span className="pill warn">needed</span>
                    )}{" "}
                    {EVIDENCE_KIND_LABEL[kind] ?? kind}
                  </span>
                  {attached ? (
                    <span className="split" style={{ gap: 8, alignItems: "center" }}>
                      {attached.locationMismatch ? (
                        <span
                          className="pill warn"
                          title="The photo's EXIF position or time disagrees with the meter or the job (D-57)"
                        >
                          location or time differs
                        </span>
                      ) : null}
                      <span className="mono faint" style={{ fontSize: ".7rem" }}>
                        {attached.verified
                          ? "sha256 verified"
                          : attached.received
                            ? "held"
                            : "file not received"}
                      </span>
                      {attached.downloadUrl ? (
                        <a
                          href={attached.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${attached.fileName}`}
                          style={{ fontSize: ".74rem" }}
                        >
                          Open
                        </a>
                      ) : null}
                    </span>
                  ) : (
                    <button
                      className="btn sm"
                      type="button"
                      onClick={() => {
                        setAttachKind(kind);
                        attachDialog.current?.showModal();
                      }}
                    >
                      Attach
                    </button>
                  )}
                </div>
              );
            })}
            {f.closeRequires.length === 0 ? (
              <div className="empty">This rule needs no evidence to close.</div>
            ) : null}
            <div style={{ marginTop: 14 }} className="eyebrow">
              Move to
            </div>
            <div className="split" style={{ marginTop: 8 }}>
              {nexts.length ? (
                nexts.map((to) => {
                  const blocked = to === "resolved" && missing.length > 0;
                  return (
                    <button
                      key={to}
                      type="button"
                      className={`btn sm${to === "resolved" ? " primary" : ""}`}
                      disabled={blocked || transition.isPending}
                      title={blocked ? "Attach the required evidence first" : undefined}
                      onClick={() => onMove(to)}
                    >
                      {STATUS_LABEL[to]}
                    </button>
                  );
                })
              ) : (
                <span className="muted" style={{ fontSize: ".84rem" }}>
                  No further moves. The next rule run verifies the fix.
                </span>
              )}
            </div>
            {missing.length > 0 && nexts.includes("resolved") ? (
              <div className="faint" style={{ fontSize: ".76rem", marginTop: 8 }}>
                Resolve unlocks when all required evidence is attached.
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <dialog className="sheet" ref={moveDialog} aria-labelledby="move-title">
        <form method="dialog" onSubmit={onMoveForm}>
          <h2 id="move-title" style={{ margin: 0 }}>
            {pendingMove ? `Move to ${STATUS_LABEL[pendingMove]}` : "Move"}
          </h2>
          {pendingMove === "dismissed" ? (
            <label>
              Reason code
              <select name="reasonCode" required defaultValue="">
                <option value="" disabled>
                  Choose a reason
                </option>
                {f.dismissalReasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {pendingMove === "awaiting_party" ? (
            <label>
              The party&apos;s request (field job or bilateral reference)
              <input name="awaitingRef" required />
            </label>
          ) : null}
          <label>
            Reason
            <textarea name="reason" required minLength={5} rows={3} />
          </label>
          <div className="split" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn sm"
              type="button"
              onClick={() => {
                moveDialog.current?.close();
                setPendingMove(null);
              }}
            >
              Cancel
            </button>
            <button className="btn sm primary" type="submit" disabled={transition.isPending}>
              Record the move
            </button>
          </div>
        </form>
      </dialog>

      <dialog className="sheet" ref={attachDialog} aria-labelledby="attach-title">
        <form method="dialog" onSubmit={(e) => e.preventDefault()}>
          <h2 id="attach-title" style={{ margin: 0 }}>
            Attach {attachKind ? (EVIDENCE_KIND_LABEL[attachKind] ?? attachKind) : "evidence"}
          </h2>
          {attachKind === "read_record" ? (
            <ReadPicker
              client={client}
              finding={f}
              onPick={(readId) => attach({ readId })}
              busy={link.isPending}
            />
          ) : (
            <>
              <HeldPicker
                held={f.heldEvidence.filter((e) => e.kind === attachKind)}
                onPick={(evidenceId) => attach({ evidenceId })}
                busy={link.isPending}
              />
              {attachKind === "photo" ||
              attachKind === "document" ||
              attachKind === "correspondence" ? (
                <UploadPicker
                  kind={attachKind}
                  busy={upload.isPending}
                  onUpload={(file) =>
                    upload.mutate(
                      { file, kind: attachKind, attachTo: [{ type: "finding", id: f.id }] },
                      {
                        onSuccess: (stored) => {
                          attachDialog.current?.close();
                          setAttachKind(null);
                          toast(
                            `${stored.fileName} scanned and stored · EvidenceAttached and FindingEvidenceLinked recorded`,
                          );
                        },
                        onError: (e) => toast(`Upload refused · ${e.message}`),
                      },
                    )
                  }
                />
              ) : null}
            </>
          )}
          <div className="split" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn sm"
              type="button"
              onClick={() => {
                attachDialog.current?.close();
                setAttachKind(null);
              }}
            >
              Close
            </button>
          </div>
        </form>
      </dialog>
      <dialog className="sheet" ref={raiseDialog} aria-labelledby="raise-job-title">
        <RaiseJobSheet
          client={client}
          findingId={f.id}
          onClose={() => raiseDialog.current?.close()}
          onDone={(job) => {
            raiseDialog.current?.close();
            toast(raisedMessage(job));
          }}
        />
      </dialog>
      <Toast />
    </>
  );
}

/** A file from the user's machine, scanned and stored before it is recorded and linked (D-54). */
function UploadPicker(props: {
  kind: "photo" | "document" | "correspondence";
  busy: boolean;
  onUpload: (file: File) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const accept =
    props.kind === "photo" ? "image/jpeg,image/png" : "application/pdf,image/jpeg,image/png";
  return (
    <div className="check" style={{ alignItems: "end" }}>
      <label style={{ flex: 1 }}>
        Upload a file
        <input
          type="file"
          accept={accept}
          onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
        />
      </label>
      <button
        className="btn sm primary"
        type="button"
        disabled={!file || props.busy}
        onClick={() => file && props.onUpload(file)}
      >
        Upload and attach
      </button>
    </div>
  );
}

function HeldPicker(props: {
  held: Evidence[];
  onPick: (evidenceId: string) => void;
  busy: boolean;
}) {
  if (props.held.length === 0)
    return (
      <div className="empty">
        The registry holds nothing of this kind for this record yet. Field records arrive with
        completed jobs.
      </div>
    );
  return (
    <div className="steps">
      {props.held.map((e) => (
        <div className="check" key={e.id}>
          <span>
            {e.fileName}
            <br />
            <span className="faint" style={{ fontSize: ".74rem" }}>
              {e.capturedAt ? shortDate(e.capturedAt) : "undated"} · {e.sha256.slice(0, 12)}…
            </span>
          </span>
          <button
            className="btn sm primary"
            type="button"
            disabled={props.busy}
            onClick={() => props.onPick(e.id)}
          >
            Attach
          </button>
        </div>
      ))}
    </div>
  );
}

/** The record's reads, newest first, to cite one as a read record (D-42). */
function ReadPicker(props: {
  client: ShellContext["client"];
  finding: FindingDetail;
  onPick: (readId: string) => void;
  busy: boolean;
}) {
  const record = useSupplyPoint(props.client, props.finding.spid ?? "");
  const meterId = record.data?.meters[0]?.id ?? null;
  const reads = useReads(
    props.client,
    meterId,
    new Date(Date.now() - 730 * 86_400_000).toISOString(),
  );
  if (!props.finding.spid)
    return <div className="empty">This finding has no supply point to take a read from.</div>;
  if (record.isPending || (meterId && reads.isPending)) return <div className="skeleton" />;
  const list: MeterRead[] = [...(reads.data ?? [])]
    .sort((a, b) => b.readAt.localeCompare(a.readAt))
    .slice(0, 8);
  if (list.length === 0)
    return <div className="empty">No reads are held for this record&apos;s meter.</div>;
  return (
    <div className="steps">
      {list.map((r) => (
        <div className="check" key={r.id}>
          <span>
            <span className="mono">
              {r.value.toLocaleString("en-GB", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 3,
              })}
            </span>{" "}
            · {r.readType.replace(/_/g, " ")}
            <br />
            <span className="faint" style={{ fontSize: ".74rem" }}>
              {shortDate(r.readAt)} · {r.source}
            </span>
          </span>
          <button
            className="btn sm primary"
            type="button"
            disabled={props.busy}
            onClick={() => props.onPick(r.id)}
          >
            Cite
          </button>
        </div>
      ))}
    </div>
  );
}
